import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { FileWithPath } from 'react-dropzone';
import { toast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { orpc } from '@/lib/api/orpc';
import { orpcClient } from '@/lib/api/orpc/client';
import { useTranslation } from '@/lib/i18n';
import { FIVE_MINUTES, remainingTimeToday } from '@/lib/query';
import { queryClient } from '@/lib/queryClient';
import { APP_ID } from '../constant';
import { getIntegratableApp, getMdmConfiguration, getPresignedUrl, getSoftwareFromAppIntegration } from './api';
import { appIntegrationKey } from './key';
import type { AppIntegrationSettingsRes, CustomAppContractState, CustomAppState, MdmActivateReq, PatchExternalTenantSettingsReq, PricingPlanState, ReAuthenticateReq, ReAuthenticateRes } from './type';
export const useAppData = (appId: string) => {
  return useQuery({
    queryKey: appIntegrationKey.integratableApp(appId),
    queryFn: () => {
      // NOTE: カスタムアプリの場合はAPIエラーになるため、nullを返す
      if (appId === APP_ID.CUSTOM_APP) return null;
      return getIntegratableApp(appId);
    },
    staleTime: remainingTimeToday(),
    select: data => data ?? null
  });
};

/**
 * Software に紐付く app-contract Overview 取得 hook (suspense)。
 *
 * route loader / 親コンポーネントが既に `softwareId` を保持している前提で、
 * `appIntegrationId` → `softwareId` の解決ラウンドトリップを省略する。
 * `appIntegrationId` を呼び出し元で必要とする場合は `software.appIntegrationId`
 * （loader で取得済み）から参照すること。
 */
export const useAppIntegrationContract = (softwareId: string) => {
  return useSuspenseQuery({
    ...orpc.software.overviewAppContract.queryOptions({
      input: {
        softwareId
      }
    }),
    staleTime: remainingTimeToday(),
    select: data => ({
      integratedApp: data.app,
      integratedTenantName: data.integratedTenantName,
      contract: data.appContract,
      isSubordinate: data.isSubordinate,
      parentApp: data.parentApp,
      canRetrieveUserGroup: data.canRetrieveUserGroup,
      canRetrieveSubGroup: data.canRetrieveSubGroup
    })
  });
};

/**
 * AppIntegration 側 monthly-cost Overview 取得 hook。
 */
export const useAppOverviewMonthlyCostQuery = (softwareId: string | undefined) => {
  return useQuery({
    ...orpc.software.overviewMonthlyCost.queryOptions({
      input: {
        softwareId: softwareId ?? ''
      }
    }),
    enabled: !!softwareId,
    staleTime: remainingTimeToday()
  });
};

/**
 * Software に紐付く AppIntegration 設定取得 hook (suspense)。
 *
 * route loader / 親コンポーネントが既に `softwareId` を保持している前提で、
 * `appIntegrationId` → `softwareId` の解決ラウンドトリップを省略する。
 */
export const useAppIntegrationSettings = (softwareId: string) => {
  return useSuspenseQuery({
    ...orpc.software.integrationSettings.queryOptions({
      input: {
        softwareId
      }
    })
  });
};
export const useMdmConfiguration = (appIntegrationId: string) => {
  return useQuery({
    queryKey: appIntegrationKey.mdmConfiguration(appIntegrationId),
    queryFn: () => getMdmConfiguration(appIntegrationId),
    staleTime: FIVE_MINUTES
  });
};

/**
 * MDM 認証情報の有効化 mutation hook。
 * `appIntegrationId` は `invalidateAppIntegration(id)` の queryKey 参照に用いる。
 */
export const useMdmActivateMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}, options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n,
    t
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async (data: MdmActivateReq) => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationMdmActivate({
        softwareId,
        ...data
      });
    },
    onSuccess: data => {
      if (data.success) {
        invalidateAppIntegration(appIntegrationId);
        options?.onSuccess?.();
        toast({
          description: t('pages.profile.app.settings.mdm.toast.updateSuccess'),
          variant: 'default'
        });
      } else {
        const errorMessage = t('pages.profile.app.settings.mdm.toast.updateError');
        options?.onError?.(errorMessage);
        toast({
          description: errorMessage,
          variant: 'destructive'
        });
      }
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      const errorMessage = message || t('pages.profile.app.settings.mdm.toast.updateError');
      options?.onError?.(errorMessage);
      toast({
        description: errorMessage,
        variant: 'destructive'
      });
    }
  });
  return {
    mutate,
    isPending
  };
};
const invalidateAppIntegration = (id: string) => {
  // REST 系 queryKey (`["app-integrations", id, ...]`) と oRPC 側 alerts 一覧
  // (`softwareContract.alertsList`) を併せて invalidate する。softwareId 単位では
  // 絞れないため router 全体を invalidate するが、ページ単位の list は軽量なので
  // 負荷影響は軽微。
  queryClient.invalidateQueries({
    queryKey: appIntegrationKey.all(id)
  });
  return queryClient.invalidateQueries({
    queryKey: orpc.software.alertsList.key()
  });
};

/**
 * 連携アプリ external-tenant 設定 mutation hook。
 * `appIntegrationId` は `invalidateAppIntegration(id)` の queryKey 参照に用いる。
 */
export const useTenantMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate
  } = useMutation({
    mutationFn: async (tenantData: PatchExternalTenantSettingsReq) => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      await orpcClient.software.integrationExternalTenant({
        softwareId,
        ...tenantData
      });
    },
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      toast({
        title: 'Success!!'
      });
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        title: message
      });
    }
  });
  return {
    mutate
  };
};

/**
 * AppIntegration deactivate mutation hook。
 */
export const useDeactivateIntegrationMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}, options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async () => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationDeactivate({
        softwareId
      });
    },
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        title: message
      });
    }
  });
  return {
    mutate,
    isPending
  };
};

/**
 * AppIntegration 削除 mutation hook。
 * `softwareId` 未解決時に呼ばれることはない（UI 側で gate）。
 */
export const useDeleteIntegrationMutator = (softwareId: string | undefined, options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async () => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationDelete({
        softwareId
      });
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate,
    isPending
  };
};

/**
 * AppIntegration reactivate mutation hook。
 */
export const useReactivateIntegrationMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}, options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async () => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationReactivate({
        softwareId
      });
    },
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate,
    isPending
  };
};

/**
 * カスタムアプリ定義（名称 / アイコン等）更新 mutation hook。
 * `appIntegrationId` は `invalidateAppIntegration(id)` の queryKey 参照に用いる。
 */
export const useUpdateCustomAppMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}, options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async (appState: CustomAppState) => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationCustomApp({
        softwareId,
        ...appState
      });
    },
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      // NOTE:
      // Custom SaaSのロゴは /software/registry 側の一覧でも表示される。
      // そちらは appIntegrationKey とは別の queryKey を使っているため、
      // ここで追加で invalidate して更新後のロゴ（timestamp query付きURL）を反映させる。
      queryClient.invalidateQueries({
        predicate: query => query.queryKey[0] === 'integratedApps' || query.queryKey[0] === 'allIntegrationApps'
      });
      options?.onSuccess?.();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate,
    isPending
  };
};
export const useUploadImageMutator = (options?: {
  onSuccess?: (data: {
    key: string;
  }) => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async (file: FileWithPath) => {
      const data = await getPresignedUrl(file.name);
      if (data) {
        await fetch(data.url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });
        return data;
      }
      throw new Error('Failed to get presigned URL');
    },
    onSuccess: data => {
      options?.onSuccess?.(data);
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate,
    isPending
  };
};

/**
 * カスタムアプリ契約（料金体系）更新 mutation hook。
 * `appIntegrationId` は `invalidateAppIntegration(id)` の queryKey 参照に用いる。
 */
export const useUpdateCustomAppContractMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}, options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async (body: CustomAppContractState) => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationCustomAppContract({
        softwareId,
        ...body
      });
    },
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate,
    isPending
  };
};

/**
 * 料金プラン（公式アプリ）更新 mutation hook。
 * `appIntegrationId` は `invalidateAppIntegration(id)` の queryKey 参照に用いる。
 */
export const usePricingPlanMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}, options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate
  } = useMutation({
    mutationFn: async (pricingData: PricingPlanState) => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationPricingPlan({
        softwareId,
        ...pricingData
      });
    },
    onSuccess: () => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate
  };
};

/**
 * AppIntegration の機能オン・オフ更新 mutation hook。
 * `appIntegrationId` は invalidate にのみ用いる。
 */
export const useAppIntegrationFeatureMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
  feature: AppIntegrationSettingsRes['features'][number]['feature'];
}, options?: {
  isEnabled: boolean;
  onSuccess?: (data: AppIntegrationSettingsRes) => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId,
    feature
  } = params;
  const {
    mutate
  } = useMutation({
    mutationFn: async () => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationFeatures({
        softwareId,
        [feature]: options?.isEnabled ?? false
      });
    },
    onSuccess: data => {
      // features は integrationSettings クエリの表示元データ。返却値で当該キャッシュを
      // 同期しないとトグル表示が古い値のまま残り、設定と表示が不整合になる（DC-8017）。
      if (softwareId) {
        queryClient.setQueriesData({
          queryKey: orpc.software.integrationSettings.key({
            input: {
              softwareId
            }
          })
        }, data);
      }
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.(data);
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate
  };
};

/**
 * 連携アプリ再認証 mutation hook。
 * `appIntegrationId` は invalidate (`invalidateAppIntegration`) のみに用いる。
 */
export const useReAuthenticateMutator = (params: {
  appIntegrationId: string;
  softwareId: string | undefined;
}, options?: {
  onSuccess?: (data: ReAuthenticateRes) => void;
  onError?: (message: string) => void;
}) => {
  const {
    i18n
  } = useTranslation();
  const {
    appIntegrationId,
    softwareId
  } = params;
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: async (data: ReAuthenticateReq) => {
      if (!softwareId) {
        throw new Error('softwareId is required');
      }
      return await orpcClient.software.integrationAuthenticate({
        softwareId,
        authMethod: data.authMethod,
        authFormValues: data.authFormValues,
        oauthRedirectQuery: data.oauthRedirectQuery
      });
    },
    onSuccess: data => {
      invalidateAppIntegration(appIntegrationId);
      options?.onSuccess?.(data);
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      options?.onError?.(message);
      toast({
        title: message
      });
    }
  });
  return {
    mutate,
    isPending
  };
};

// Software data hooks

/**
 * AppIntegration ↔ Software の解決を行う queryOptions factory。
 *
 * **本 factory は `/app-integrations/$id/...` 配下の旧 URL を `/software/$softwareId/...`
 * に redirect する backward-compat ルート (`routes/_staff/app-integrations/$id/_$id.tsx`
 * 等) 専用**。アプリ機能側は route loader で取得済みの `software` / `softwareId` を
 * 直接利用すること（再ラウンドトリップ防止のため redirect 用以外では使用しない）。
 *
 * すべての AppIntegration は Software 紐付け済みである前提（移行完了済み）のため
 * GET のみ。失敗時は呼び出し側でエラーハンドリングする。
 */
export const softwareFromAppIntegrationQueryOptions = (appIntegrationId: string) => ({
  queryKey: appIntegrationKey.software(appIntegrationId),
  queryFn: () => getSoftwareFromAppIntegration(appIntegrationId),
  staleTime: remainingTimeToday()
}) as const;